/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector, .js-option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;
      this.isFeaturedProduct = !!this.dataset.featuredProduct;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      if (this._isSyncingOptions) return;

      this.selectFirstAvailableSubsequentOptions(evt);

      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice(this.section.querySelector('.js-product-price > .price'));
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateStickyAtc();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Returns option selectors that contribute to the selected options array.
     * @returns {Element[]}
     */
    getContributingSelectors() {
      const contributingSelectors = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          if (!selector.classList.contains('dual-option-selector')) {
            contributingSelectors.push(selector);
          }
        } else {
          contributingSelectors.push(selector);
        }
      });

      return contributingSelectors;
    }

    /**
     * Finds the option index that triggered the change event.
     * @param {object} evt - Event object.
     * @returns {number}
     */
    getChangedOptionIndex(evt) {
      const contributingSelectors = this.getContributingSelectors();
      let changedSelector = evt.target.closest('.option-selector, .js-option-selector');

      if (changedSelector?.classList.contains('dual-option-selector')
        && changedSelector.dataset.selectorType === 'dropdown') {
        const dualParent = changedSelector.closest('dual-swatch-selector');
        changedSelector = dualParent?.querySelector('.option-selector[data-selector-type="listed"]') || changedSelector;
      }

      return contributingSelectors.indexOf(changedSelector);
    }

    /**
     * When an option changes, auto-select the first available values for later
     * options (e.g. color change → first available weight).
     * @param {object} evt - Event object.
     */
    selectFirstAvailableSubsequentOptions(evt) {
      const changedIndex = this.getChangedOptionIndex(evt);
      if (changedIndex < 0) return;

      const contributingSelectors = this.getContributingSelectors();
      if (changedIndex >= contributingSelectors.length - 1) return;

      const selectedOptions = this.getSelectedOptions();

      const matchingVariant = this.data.product.variants.find((variant) => {
        if (!variant.available) return false;

        for (let i = 0; i <= changedIndex; i += 1) {
          if (selectedOptions[i] !== null && variant.options[i] !== selectedOptions[i]) {
            return false;
          }
        }

        return true;
      }) || this.data.product.variants.find((variant) => {
        for (let i = 0; i <= changedIndex; i += 1) {
          if (selectedOptions[i] !== null && variant.options[i] !== selectedOptions[i]) {
            return false;
          }
        }

        return true;
      });

      if (!matchingVariant) return;

      const needsUpdate = matchingVariant.options.some(
        (value, index) => index > changedIndex && value !== selectedOptions[index]
      );
      if (!needsUpdate) return;

      this._isSyncingOptions = true;

      try {
        for (let i = changedIndex + 1; i < contributingSelectors.length; i += 1) {
          const value = matchingVariant.options[i];
          if (!value) continue;

          this.applyOptionValue(contributingSelectors[i], value);
        }
      } finally {
        this._isSyncingOptions = false;
      }
    }

    /**
     * Applies an option value to a selector without re-entering change handling.
     * @param {Element} selector - Option selector element.
     * @param {string} value - Option value to select.
     */
    applyOptionValue(selector, value) {
      if (selector.dataset.selectorType === 'dropdown') {
        const customSelect = selector.querySelector('custom-select');
        const optionEl = Array.from(selector.querySelectorAll('.custom-select__option')).find(
          (opt) => opt.dataset.value === value
        );

        if (customSelect && optionEl && typeof customSelect.selectOption === 'function') {
          const previousSelected = customSelect.selectedOption;
          if (optionEl !== previousSelected) {
            optionEl.setAttribute('aria-selected', 'true');
            previousSelected?.setAttribute('aria-selected', 'false');

            if (customSelect.swatches) {
              const buttonSwatch = customSelect.button.querySelector('.custom-select__swatch');
              const optionSwatch = optionEl.querySelector('.custom-select__swatch');
              if (buttonSwatch && optionSwatch) {
                buttonSwatch.outerHTML = optionSwatch.outerHTML;
              }
              const buttonLabel = customSelect.button.querySelector('span:last-of-type');
              const optionLabel = optionEl.querySelector('span:last-of-type');
              if (buttonLabel && optionLabel) {
                buttonLabel.textContent = optionLabel.textContent;
              }
              customSelect.button.classList.toggle('is-unavailable', optionEl.classList.contains('is-unavailable'));
            } else {
              const buttonLabel = customSelect.button.querySelector('span:last-of-type');
              if (buttonLabel && optionEl.firstElementChild) {
                buttonLabel.textContent = optionEl.firstElementChild.textContent;
              }
            }

            customSelect.listbox?.setAttribute('aria-activedescendant', optionEl.id);
            customSelect.selectedOption = optionEl;

            if (customSelect.nativeSelect) {
              customSelect.nativeSelect.value = value;
            }
          }
        }
      } else {
        const input = Array.from(selector.querySelectorAll('input.js-option')).find(
          (opt) => opt.value === value
        );

        if (input && !input.checked) {
          input.checked = true;

          const colorText = selector.querySelectorAll('.js-color-text');
          const valueEl = input.nextElementSibling?.querySelector('.js-value');
          if (colorText.length && valueEl) {
            colorText.forEach((el) => {
              el.textContent = valueEl.textContent;
            });
          }
        }
      }
    }

    /**
     * Updates the state of the sticky ATC panel
     */
    updateStickyAtc() {
      this.updatePrice(document.querySelector('.js-atc-price > .price'));

      const stickyAtcVariant = document.querySelector('.js-atc-selected-variant');
      if (stickyAtcVariant) {
        stickyAtcVariant.innerHTML = this.variant ? this.variant.title : '';
      }

      const stickyAtcVariantSwatch = document.querySelector('.js-atc-selected-variant-swatch');
      const activeSwatch = this.querySelector('.opt-btn:checked + .opt-label--swatch');
      if (stickyAtcVariantSwatch && activeSwatch) {
        stickyAtcVariantSwatch.innerHTML = activeSwatch.outerHTML;
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      this.stickyAddBtn = this.stickyAddBtn || document.querySelector('.js-atc-button');

      const variantAvailable = this.variant && this.variant.available;
      const priceIsZero = Boolean(this.variant && Number(this.variant.price) === 0);
      const atcRoot = this.section.closest('.cc-main-product') || this.section;
      atcRoot.querySelectorAll('.js-add-to-cart-row').forEach((el) => {
        el.classList.toggle('js-zero-price-hidden', priceIsZero);
      });

      if (!this.addBtn) return;

      this.addBtn.disabled = !variantAvailable || this.preSelection;
      if (this.stickyAddBtn) this.stickyAddBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
        if (this.stickyAddBtn) this.stickyAddBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;

        if (this.stickyAddBtn) {
          const addToCartText = variantAvailable ? this.stickyAddBtn.dataset.addToCartText : unavailableStr;
          this.stickyAddBtn.innerHTML = `
              <span class="hidden md:block">${addToCartText}</span>
              <span class="large-text align-top md:hidden">${this.stickyAddBtn.dataset.plusIcon}</span>
          `;
        }
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const customSelectBtn = optionEl.closest('.custom-select')?.querySelector('.custom-select__btn');
        const customSelectBtnValue = customSelectBtn?.textContent?.trim() ?? '';
        const optionValue = optionEl.dataset?.value?.trim() ?? '';
        if (customSelectBtnValue && optionValue && customSelectBtnValue === optionValue) {
          customSelectBtn.classList.toggle('is-unavailable', !available);
        }

        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        let selectorIndexOffset = 0;

        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          selectorIndex = selectorIndex - selectorIndexOffset;
          if (selector.classList.contains('dual-option-selector')) {
            selectorIndexOffset = 1;
          }

          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        let selectorIndexOffset = 0;

        this.optionSelectors.forEach((selector, selectorIndex) => {
          selectorIndex = selectorIndex - selectorIndexOffset;

          if (selector.classList.contains('dual-option-selector')) {
            selectorIndexOffset = 1;
          }

          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.dual-option-selector') || evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelectorAll('.js-color-text');
      colorText.forEach((el) => {
        el.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
      });
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
      if (!this.mediaGallery) return;

      const variantMedia = this.mediaGallery.querySelector(
        `[data-media-id="${this.variant.featured_media.id}"]`
      );
      this.mediaGallery.setActiveMedia(variantMedia, true, true);
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice(priceElem) {
      if (!priceElem) return;

      let {variant} = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = priceElem.querySelector('.price__current');
        const priceWasEl = priceElem.querySelector('.price__was');
        const unitPriceEl = priceElem.querySelector('.unit-price');
        const discountEl = priceElem.querySelector('.price__discount');
        const {price, compare_at_price: compareAtPrice} = this.variant;

        // Update price.
        if (variant.compare_at_price > variant.price) {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.salePrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
            priceWasEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
          }
        } else {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = '';
            priceWasEl.querySelector('.js-value').innerHTML = '';
          }
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = priceElem.querySelector('.unit-price__price');
          const unitEl = priceElem.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        priceWasEl.hidden = !(compareAtPrice > price);
        priceElem.classList.toggle('price--on-sale', compareAtPrice > price);
        priceElem.classList.toggle('price--sold-out', !this.variant.available);

        if (compareAtPrice > price) {
          const discountPercentage = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
          discountEl.textContent = theme.strings.discount.replace('[discount]', discountPercentage);
          discountEl.hidden = false;
        } else {
          discountEl.hidden = true;
        }
      } else {
        priceElem.classList.remove('price--on-sale');
        priceElem.classList.remove('price--sold-out');
      }

      priceElem.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      priceElem.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = priceElem.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false' || this.isFeaturedProduct) return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          if (!selector.classList.contains('dual-option-selector')) {
            const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
            selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
          }
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
